import { css } from "@linaria/core";
import { combineTokens } from "../css/util.combine-tokens";
import { units } from "../css-tokens/units";
import { colors } from "../design-tokens/colors";
import { fonts } from "../design-tokens/fonts";
import { surfaces } from "../design-tokens/surfaces";
import { makeSelectors } from "../css/maker.selectors";
import { outline } from "../css-tokens/mixins/outline";
import { easings } from "../css-tokens/easings";

export const iconTransitionDurationMs = 150;

export const tokens = combineTokens("--icon-button", {
  color: colors.gray.sim100,
});

export const modifiers = makeSelectors({
  small: css``,
  withLabel: css``,
});

export const buttonCls = css`
  ${outline()};

  padding: ${units.px(8)} ${units.px(10)};

  box-sizing: border-box;
  max-width: 100%;
  width: max-content;
  min-height: ${units.px(40)};
  min-width: ${units.px(40)};

  background-color: transparent;
  border-radius: ${units.px(4)};

  display: flex;
  flex-shrink: 0;
  justify-content: center;
  align-items: center;
  gap: ${units.px(15)};

  font-family: ${fonts.body()};
  font-size: ${units.font.px(15)};
  color: ${tokens.color()};

  transition: color 0.1s linear, background-color 0.1s linear;

  &${modifiers.withLabel()} {
    justify-content: flex-start;
  }

  & svg {
    display: block;
  }

  &${modifiers.small()} {
    padding: ${units.px(5)};

    min-height: ${units.px(30)};
    min-width: ${units.px(30)};
  }

  &[data-state="default"] {
    &[data-active="true"] {
      background-color: ${colors.gray.alpha06()};
    }

    @media (pointer: fine) {
      &:hover {
        background-color: ${colors.black({ alpha: 0.06 })};
      }
    }
  }

  &[data-state="success"] {
    background: ${surfaces.success()};
  }
`;

export const iconsContainerCls = css`
  position: relative;

  display: flex;
  justify-content: center;
  align-items: center;

  transition: color 100ms linear, opacity 100ms linear;

  .${buttonCls}:not(${modifiers.withLabel()}) & {
    opacity: 0.6;
  }

  .${buttonCls}[data-active="true"] && {
    opacity: 1;
  }

  .${buttonCls}[data-state="default"][data-active="true"] & {
    color: ${colors.accent()};
  }

  @media (pointer: fine) {
    .${buttonCls}:hover && {
      opacity: 1;
    }

    .${buttonCls}[data-state="default"][data-active="true"]:hover & {
      color: ${surfaces.accent({ state: "active", property: "background" })};
    }
  }
`;

export const iconWrapperCls = css`
  height: ${units.px(20)};
  width: ${units.px(20)};

  display: flex;
  justify-content: center;
  align-items: center;

  &-enter {
    opacity: 0;
    transform: scale(0.5);

    &-active {
      opacity: 1;
      transform: scale(1);
      transition: opacity ${iconTransitionDurationMs}ms linear,
        transform ${iconTransitionDurationMs}ms ${easings.outQuad};
    }
  }

  &-exit {
    position: absolute;
    inset: 0;

    display: flex;
    flex-shrink: 0;
    justify-content: center;
    align-items: center;

    opacity: 1;
    transform: scale(1);

    &-active {
      opacity: 0;
      transform: scale(0.5);
      transition: opacity ${iconTransitionDurationMs}ms linear,
        transform ${iconTransitionDurationMs}ms ${easings.outQuad};
    }
  }
`;

export const throbberCls = css`
  box-sizing: border-box;
  height: 100%;
  width: 100%;

  border: ${units.px(1.5)} solid ${colors.gray.alpha06()};
  border-radius: 50%;
  border-left-color: ${colors.black()};

  display: block;

  animation: spin 0.9s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }

    100% {
      transform: rotate(360deg);
    }
  }
`;

export const successCls = css`
  box-sizing: border-box;
  height: 100%;
  width: 100%;

  border: ${units.px(1.5)} solid ${colors.success()};
  border-radius: 50%;

  display: grid;
  place-items: center;

  color: ${colors.success()};
`;

export const errorCls = css`
  box-sizing: border-box;
  height: 100%;
  width: 100%;

  border: ${units.px(1.5)} solid ${colors.danger()};
  border-radius: 50%;

  display: block;

  color: ${colors.danger()};

  &::before,
  &::after {
    content: "";

    position: absolute;
    top: 50%;
    left: 50%;

    height: ${units.px(1.5)};
    width: ${units.px(10)};

    background: currentColor;

    transform: translate(-50%, -50%) rotate(45deg);
  }

  &::after {
    transform: translate(-50%, -50%) rotate(-45deg);
  }
`;

export const label = css`
  display: flex;
  gap: ${units.px(6)};
`;

export const checkmark = css`
  position: absolute;
  right: ${units.px(-4)};
  bottom: ${units.px(-4)};

  height: ${units.px(16)};
  width: ${units.px(16)};

  background: ${surfaces.graphicalSuccess()};
  border-radius: 50%;

  display: flex;
  justify-content: center;
  align-items: center;
`;

export const selectors = makeSelectors({
  button: buttonCls,
  iconsContainer: iconsContainerCls,
  iconWrapper: iconWrapperCls,
  throbber: throbberCls,
});
